//
// Copyright (c) 2024 Comcast
//

import PropTypes from 'prop-types'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Field } from 'react-final-form'
import styled, { css } from 'styled-components'
import CreatableSelect from 'react-select/creatable'

import { readFile, utils } from 'xlsx'

import colors from 'components/utils/colors'

import SelectControl from 'components/Shared/Select/components/SelectControl'
import Menu from 'components/Shared/Select/components/Menu'
import ClearIndicator from 'components/Shared/Select/components/ClearIndicator'
import Input from 'components/Shared/Select/components/Input'
import Option from 'components/Shared/Select/components/Option'
import MultiValueContainer from 'components/Shared/Select/components/MultiValueContainer'
import MultiValueLabel from 'components/Shared/Select/components/MultiValueLabel'
import MultiValueRemove from 'components/Shared/Select/components/MultiValueRemove'

import IconLink from 'components/Shared/IconLink/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import FileInput from 'components/Shared/Form/FileInput/Index'

const PORTAL_Z_INDEX = 10001

const components = {
  DropdownIndicator: null,
  Control: SelectControl,
  Menu: Menu,
  ClearIndicator: ClearIndicator,
  Input: Input,
  Option: Option,
  MultiValueContainer: MultiValueContainer,
  MultiValueLabel: MultiValueLabel,
  MultiValueRemove: MultiValueRemove,
}

const StyledWrapper = styled.div`
  display: flex;
  flex-flow: row;
  align-items: start;
  ${props => props.maxItems && css`
    margin-bottom: 9px;
  `}
`

const SelectWrapper = styled.div`
  position: relative;
  flex: 1;
`

const StyledCreatableSelect = styled(CreatableSelect)`
  ${props => props.$allowCsvUpload && css`
    margin-right: 16px;
    flex: 1;
  `}
`

const StyledTextWithIcon = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 4px;
  min-width: 80px;
`

const StyledText = styled.span`
  line-height: 20px;
`

const ItemsCountLabel = styled.div`
  position: absolute;
  bottom: -18px;
  right: 16px;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  color: ${props => props.hasTooManyItems ? colors.alert65 : colors.secondary60};
`

const FieldComponent = ({ value, onChange, name, disabled, placeholder, isInModal, maxItems, isError, allowCsvUpload }) => {
  const fileInput = useRef(null)
  const { t } = useTranslation()

  const handleFileChange = async (e) => {
    const file = e.target.files[0]

    if (!file) {
      return
    }

    const fileData = await file.arrayBuffer()

    const workbooks = readFile(fileData)
    const sheetName = workbooks.SheetNames[0]
    const sheetData = workbooks.Sheets[sheetName]
    const jsonSheetData = utils.sheet_to_json(sheetData)

    let result = []

    if (jsonSheetData.length) {
      const headers = Object.keys(jsonSheetData[0])
      const body = jsonSheetData.flatMap(row => Object.values(row)[0])

      result = headers.concat(body)
    }

    onChange(result)
  }

  const handleChange = (values) => {
    const data = values.map(v => v.value || v)

    onChange(data)
  }

  let itemsCountText
  let hasTooManyItems = false

  if (maxItems) {
    const itemsCount = maxItems - value.length

    if (itemsCount === maxItems) {
      itemsCountText = t('tableFormFieldsBuilder.creatableArrayField.itemsMax', { count: maxItems })
    } else if (itemsCount >= 0) {
      itemsCountText = t('tableFormFieldsBuilder.creatableArrayField.itemsLeft', { count: itemsCount })
    } else {
      hasTooManyItems = true
      itemsCountText = t('tableFormFieldsBuilder.creatableArrayField.itemsOver', { count: -itemsCount })
    }
  }

  const tooltipId = name + '_tooltip'

  return (
    <>
      <SelectWrapper>
        <StyledCreatableSelect
          small
          isMulti
          openMenuOnClick={false}
          isError={isError}
          value={value}
          onChange={(values) => handleChange(values)}
          placeholder={placeholder || t('tableFormFieldsBuilder.creatableArrayField.placeholderAddItems')}
          usePortal={isInModal ? true : undefined}
          portalZindex={isInModal ? PORTAL_Z_INDEX : undefined}
          isDisabled={disabled}
          classNamePrefix='react-select'
          components={components}
          getOptionValue={(option) => option.value || option}
          getOptionLabel={(option) => option.label || option}
          noOptionsMessage={() => t('tableFormFieldsBuilder.creatableArrayField.optionsEmpty')}
          formatCreateLabel={(val) => `${t('tableFormFieldsBuilder.creatableArrayField.optionsAdd')} "${val}"`}
          $allowCsvUpload={allowCsvUpload}
        />
        {!!maxItems && <ItemsCountLabel hasTooManyItems={hasTooManyItems} data-test='items-count-label'>{itemsCountText}</ItemsCountLabel>}
      </SelectWrapper>

      {(!disabled && allowCsvUpload) && (
        <StyledTextWithIcon>
          <StyledText>{t('tableFormFieldsBuilder.creatableArrayField.text')}</StyledText>
          <IconLink
            data-tooltip-content={t('tableFormFieldsBuilder.creatableArrayField.tooltip')}
            data-tooltip-id={tooltipId}
            iconName='icon-add-big-16'
            onClick={() => fileInput.current.click()}
            disabled={disabled}
          />
          <Tooltip id={tooltipId} />
          <FileInput
            ref={fileInput}
            onChange={(e) => handleFileChange(e)}
            accept='.xlsx, .csv'
          />
        </StyledTextWithIcon>
      )}
    </>
  )
}

FieldComponent.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  isInModal: PropTypes.bool,
  maxItems: PropTypes.number,
  isError: PropTypes.bool,
  allowCsvUpload: PropTypes.bool,
}

const CreatableArrayField = ({ name, placeholder, validate, isInModal, disabled, maxItems, allowCsvUpload }) => {
  const fieldValidate = (value) => {
    // ignore string values
    if (typeof value === 'string') {
      return
    }

    let error = validate(value)
    const itemsCount = value?.length || 0
    if (!error && maxItems && (itemsCount > maxItems)) {
      error = { type: 'HAS_TOO_MANY_ITEMS_ERROR', highlight: true }
    }
    return error
  }

  return (
    <StyledWrapper maxItems={maxItems}>
      <Field name={name} validate={fieldValidate}>
        {({ input, meta }) => {
          let value = input.value

          // currently we get strings for this field which we should ignore for
          // correct work and error validation
          if (typeof value === 'string') {
            value = []
          }

          return (
            <FieldComponent
              value={value}
              onChange={input.onChange}
              name={input.name}
              disabled={disabled}
              placeholder={placeholder}
              isInModal={isInModal}
              maxItems={maxItems}
              isError={!!meta.error?.highlight}
              allowCsvUpload={allowCsvUpload}
            />
          )
        }}

      </Field>
    </StyledWrapper>
  )
}

CreatableArrayField.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  validate: PropTypes.func,
  isInModal: PropTypes.bool,
  disabled: PropTypes.bool,
  maxItems: PropTypes.number,
  allowCsvUpload: PropTypes.bool,
}

export default CreatableArrayField
